import { v4 } from "uuid";
import { ITracking, TLogInfo, TLogWarn, TLogError, TLogger, assertTracking, isFargate } from "bridging";

const processError = (error?: unknown) => {
  if (typeof error === "object" && error !== null) {
    const retval: Record<string, unknown> = {};
    if ('stack' in error) {
      retval.__error_stack = error.stack;
    }
    if ('code' in error) {
      retval.__error_code = error.code;
    }
    retval.__error_message = error.toString();
    // retval.__error_stack = error as Record<string, unknown>["stack"];
    // retval.__error_code = error as Record<string, unknown>["code"];
    // retval.__error_message = error as Record<string, unknown>["message"];

    let cause = 'cause' in error ? error.cause : null;
    while (typeof cause === "object" && cause !== null) {
      if ('stack' in cause) {
        retval.__error_stack ??= "";
        retval.__error_stack = retval.__error_stack + "\n" + cause.stack;
      }
      if ('code' in cause) {
        retval.__error_code ??= "";
        retval.__error_code = retval.__error_code + "\n" + cause.code;
      }
      retval.__error_message = retval.__error_message + "\n" + error.toString();
      cause = 'cause' in cause ? cause.cause : null;
    }

    return retval;
  }

  return { error: error };
};

const processLog = (tracking: ITracking, o: Partial<TLogError> | string, level: string) => {
  const message = makeLogEntry(tracking, o);
  if (!isFargate()) {
    return message;
  }

  return JSON.stringify(wrapLogEntry(message, level))
}

const makeLogEntry = (tracking: ITracking, o: Partial<TLogError> | string) => {
  let fields = {} as Record<string, unknown>;
  if (typeof o === 'string') {
    fields["message"] = o;
  } else {
    fields = {
      ...o,
      ...processError(o.error),
    };
  }
  const message = {
    ...fields,
    trackingId: tracking.trackingId,
    trackingPath: tracking.trackingPath,
  };

  return message;
};

// so sometimes we want high precision time in the metrics as values can overwrite each other
// I tested below for perf and 1000000 iterations took 0.5 seconds.
const getHrIso = () => {
  const isoDateWithoutZ = new Date().toISOString().slice(0, -1);

  // Get the current time in nanoseconds using process.hrtime.bigint
  // this hrtime is kinda arbitrary and mostly used for diffing, it's not like epoch!
  let nanoseconds = 0n;
  if (process.hrtime?.bigint) {
    const hrTime = process.hrtime.bigint();
    nanoseconds = hrTime % 1000000n;  // Remainder gives you the nanoseconds part
  }

  // Append the nanoseconds to the ISO date string
  return `${isoDateWithoutZ}${String(nanoseconds).padStart(6, '0')}Z`;
}

// fargate and metrics need to bring their own wrapper, lambda does it with console log.
const wrapLogEntry = <T extends object>(message: T, level: string) => {
  return {
    timestamp: getHrIso(),
    level: level,
    message,
  }
}

const makeMetric = (tracking: ITracking, level: string, code: string, extra?: Record<string, string | number>) => {
  const entry = { extra, code };
  const wrapped = { ...wrapLogEntry(entry, level), trackingId: tracking.trackingId, trackingPath: tracking.trackingPath };
  return wrapped;
}

const logLevels = ["DEBUG", "INFO", "WARN", "ERROR"] as const;
type TLogLevel = typeof logLevels[number];
const logLevelCheck = (inputLevel: (typeof logLevels)[number]) => {
  const logLevel = logLevels.indexOf(inputLevel);
  const minLevel = (logLevels.indexOf(process.env["LOG_LEVEL"] as TLogLevel || "DEBUG"));

  return logLevel >= minLevel; // false if should not be logged
}
export const topLevelLogger = (what: string) => getLogger({ trackingId: v4(), trackingPath: what });

// function to proxy log calls to multiple loggers. tracking, metrics and child loggers forwarded only to the first logger

export const getLogger = (tracking: ITracking, dynamicLogger?: TLogger): TLogger => ({
  getTracking: () => tracking,
  dynamic: dynamicLogger,
  makeChildLogger: (nextSlug: string) => {
    const childDynamicLogger = dynamicLogger ? dynamicLogger.makeChildLogger(nextSlug) : undefined;
    return getLogger({ ...tracking, trackingPath: `${tracking.trackingPath}-${nextSlug}` }, childDynamicLogger)
  },
  debug: (o: TLogInfo | string) => {
    dynamicLogger?.debug(o);
    if (logLevelCheck("DEBUG")) {
      console.debug(processLog(tracking, o, "DEBUG"))
    }
  },
  info: (o: TLogInfo | string) => {
    dynamicLogger?.info(o);
    if (logLevelCheck("INFO")) {
      console.log(processLog(tracking, o, "INFO"))
    }
  },
  warn: (o: TLogWarn | string) => {
    dynamicLogger?.warn(o);
    if (logLevelCheck("WARN")) {
      console.warn(processLog(tracking, o, "WARN"))
    }
  },
  error: (o: TLogError) => {
    dynamicLogger?.error(o);
    console.error(processLog(tracking, o, "ERROR"))
  },
  makeMetric: {
    debug: (code: string, extra?: Record<string, string | number>) => makeMetric(tracking, "DEBUG", code, extra),
    info: (code: string, extra?: Record<string, string | number>) => makeMetric(tracking, "INFO", code, extra),
    warn: (code: string, extra?: Record<string, string | number>) => makeMetric(tracking, "WARN", code, extra),
    error: (code: string, extra?: Record<string, string | number>) => makeMetric(tracking, "ERROR", code, extra),
  }
});

export const updateTrackingPathInplace = (
  tracking: ITracking,
  slug: string | number,
) => {
  tracking.trackingPath ??= "unknown";
  tracking.trackingId ??= v4();
  tracking.trackingPath = `${tracking.trackingPath}-${slug}`;
};

// good to use this in a catch block as it will always provide a logger from a string
export const safeGetLogger = (body: string) => {
  let logger: ReturnType<typeof getLogger>;
  try {
    const payload = assertTracking(JSON.parse(body));
    logger = getLogger(payload);
  } catch {
    logger = getLogger({
      trackingId: "unavailable",
      trackingPath: "unavailable",
    });
  }

  return logger;
};
